import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Filter as FilterIcon } from 'lucide-react';
import BrandForm from './BrandForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import ExternalLink from '../../../shared/ExternalLink';
import RecordChip from '../../../shared/RecordChip';
import ParagraphText from '../../../shared/ParagraphText';
import { ActiveStatusPill, CategoryPill, Pill } from '../../../shared/Pill';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import '../../../shared/ListRowHover.css';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { ownerName, ownerHref } from '../../../shared/brandLabel';
import { askConfirm } from '../../../shared/AppDialogs';

const ORIGIN = { label: 'Brands', path: '/inventory-brands' };
const FILTER_SCREEN_KEY = 'inventory-brands';
const DEFAULT_FILTERS = { status: 'all', brand_type_id: '' };
const FIELD_FILTER_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_FILTER_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

function BrandTable() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate, canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  // Lazy-init so Brand Types' "In Use" deep link (?brand_type=<id>) is in
  // the first fetch, same as ProductTable.js. A dashboard tile can add
  // more filters (initialFilters).
  const location = useLocation();
  const [filters, setFilters] = useState(() => {
    const brandTypeId = searchParams.get('brand_type');
    return { ...DEFAULT_FILTERS, ...(location.state?.initialFilters || {}), ...(brandTypeId ? { brand_type_id: brandTypeId } : {}) };
  });
  const { status, brand_type_id: brandTypeFilter } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const [filterDraft, setFilterDraft] = useState(null); // null = panel closed
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };
  const [brandTypes, setBrandTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  // The Brand whose full Description shows under its row (one at a time).
  const [expandedId, setExpandedId] = useState(null);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/brand-types', { params: { status: 'all' } })
      .then((res) => setBrandTypes(res.data.data || []))
      .catch(() => setBrandTypes([]));
  }, []);

  // Deep link from an Organisation's / Company Information's Brands card:
  // /inventory-brands?edit=<id> opens straight to that Brand's edit form.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    if (canUpdate) {
      axios.get(`/api/brands/${editId}`)
        .then((res) => handleEdit(res.data.data))
        .catch((err) => showError(err, 'Could not open this Brand'));
    }
    setSearchParams((params) => { params.delete('edit'); return params; }, { replace: true });
    // eslint-disable-next-line
  }, [searchParams]);

  // Scrub the consumed deep-link param once mount has read it.
  useEffect(() => {
    if (!searchParams.get('brand_type')) return;
    setSearchParams((params) => { params.delete('brand_type'); return params; }, { replace: true });
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, page, limit, brandTypeFilter]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/brands', { params: { status, page, limit, brand_type_id: brandTypeFilter || undefined } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Brand "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/brands/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/brands/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleDelete = async (item) => {
    if (!(await askConfirm(`Permanently delete Brand "${item.name}"? This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/brands/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/brands/${editItem.id}`, data);
      } else {
        await axios.post('/api/brands', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="inactive">Deactivated</option>
        </select>
      </div>
      <div>
        <label style={FIELD_FILTER_LABEL_STYLE}>Brand Type</label><br />
        <select className="styled-input" value={draft.brand_type_id || ''} onChange={(e) => setDraftField('brand_type_id', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
          <option value="">All types</option>
          {brandTypes.map((t) => (
            <option key={t.id} value={String(t.id)}>{t.name}{t.is_active ? '' : ' (deactivated)'}</option>
          ))}
        </select>
      </div>
    </>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Brands</h2>
        {canCreate && (
          <button
            onClick={handleAdd}
            disabled={showForm}
            title={showForm ? 'Finish editing first' : undefined}
            style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
          >
            Add Brand
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div style={{ margin: '14px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Brands list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <BrandForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            origin={ORIGIN}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '18%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '6%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Owner</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Type</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>URL</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Description</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <React.Fragment key={item.id}>
              <tr
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>
                  {item.name}
                  {item.show_on_website && (
                    <span style={{ marginLeft: 8 }}>
                      <Pill color="#1b6e3a" background="#e3f4e8" title={`Shown on the website at /brands/${item.web_slug}`}>On website</Pill>
                    </span>
                  )}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 13 }}>
                  {item.owner ? (
                    <RecordChip
                      label={ownerName(item.owner)}
                      href={ownerHref(item.owner)}
                      icon={Building2}
                      origin={ORIGIN}
                      title="Open owner"
                      size="sm"
                    />
                  ) : <span style={{ color: '#b26a00' }}>No owner</span>}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 13 }}>
                  {item.brand_type ? <CategoryPill value={item.brand_type.name} /> : <span style={{ color: '#b26a00' }}>No type</span>}
                </td>
                <td style={{ padding: '6px 8px', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                  {item.url ? <ExternalLink href={item.url} maxWidth="100%" /> : '—'}
                </td>
                <td
                  style={{ padding: '6px 8px', fontSize: 13, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: item.description ? 'pointer' : undefined }}
                  title={item.description ? (expandedId === item.id ? 'Hide the full description' : 'Show the full description') : undefined}
                  onClick={item.description ? (e) => { e.stopPropagation(); setExpandedId(expandedId === item.id ? null : item.id); } : undefined}
                >
                  {item.description || '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                  {item.usage_count ? (
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
                      title="See which Products use this Brand"
                      onClick={() => navigate(`/products?brand=${item.id}`)}
                    >
                      {item.usage_count}
                    </button>
                  ) : '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  <ActiveStatusPill active={item.is_active} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      item.is_active
                        ? {
                            key: 'deactivate',
                            label: 'Deactivate',
                            danger: true,
                            disabled: showForm,
                            disabledTitle: showForm ? 'Finish editing first' : undefined,
                            onClick: () => handleDeactivate(item),
                          }
                        : {
                            key: 'reactivate',
                            label: 'Reactivate',
                            disabled: showForm,
                            disabledTitle: showForm ? 'Finish editing first' : undefined,
                            onClick: () => handleReactivate(item),
                          },
                      ...(item.is_active ? [] : [{
                        key: 'delete',
                        label: 'Delete',
                        danger: true,
                        disabled: showForm,
                        disabledTitle: showForm ? 'Finish editing first' : undefined,
                        onClick: () => handleDelete(item),
                      }]),
                    ]}
                  />
                </td>
              </tr>
              {expandedId === item.id && item.description && (
                <tr>
                  <td colSpan={8} style={{ padding: '10px 16px 14px', background: '#f7f9fc', borderBottom: '1px solid #e3e7ee' }}>
                    <ParagraphText text={item.description} style={{ fontSize: 13, color: '#444', maxWidth: 820 }} />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Brands"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default BrandTable;
